import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Editor } from '@toast-ui/react-editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import colorSyntax from '@toast-ui/editor-plugin-color-syntax';
import 'tui-color-picker/dist/tui-color-picker.css';
import { useNavigate, useLocation } from 'react-router-dom';
import MyCalendar from '../../MyCalendar/MyCalendar';

// ✅ STEP1 상세패널 아이콘 (CNC 스타일)
const FacilityDetailIcon = ({ type }) => {
  return (
    <div className={`facility_detail_icon type_${type}`}>
      {type === 'people' && (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="9" cy="9" r="3" />
          <circle cx="16.5" cy="10" r="2.5" />
          <path d="M3.5 19c0-2.8 2.5-5 5.5-5s5.5 2.2 5.5 5" />
          <path d="M13.5 19c.2-2 1.9-3.6 4-3.9 2.2.3 3.5 1.7 3.5 3.9" />
        </svg>
      )}

      {type === 'fee' && (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2l8 4v6c0 5-3.5 9.4-8 10-4.5-.6-8-5-8-10V6l8-4z" />
          <path d="M9 12h6" />
        </svg>
      )}

      {type === 'badge_red' && (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2l2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5L4.8 7.2l5-.7L12 2z" />
        </svg>
      )}

      {type === 'badge_gray' && (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2l2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5L4.8 7.2l5-.7L12 2z" />
        </svg>
      )}

      {type === 'sub' && (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="12" rx="2" />
          <line x1="3" y1="13" x2="21" y2="13" />
          <rect x="9" y="18" width="6" height="2" />
        </svg>
      )}
    </div>
  );
};

const FacilityWrite = () => {
  const topRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [select, setSelect] = useState(0);
  const [locationKey, setLocationKey] = useState('');

  // 캘린더 데이터
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);

  const [selectStart, setSelectStart] = useState('');
  const [selectEnd, setSelectEnd] = useState('');

  const [facility_code_list, setFacility_code_list] = useState([]);
  const [selectFacility, setSelectFacility] = useState(null);
  const [step, setStep] = useState(1);
  const [reservation_name, setReservation_name] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isDateCheck, setIsDateCheck] = useState(false);

  const [timeData, setTimeData] = useState([9, 10, 11, 12, 13, 14, 15, 16, 17, 18]);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchOption, setSearchOption] = useState('연락처');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 6;

  const [reservedList, setReservedList] = useState([]); // 해당 시설 예약들
  const [isOverlap, setIsOverlap] = useState(false); // 현재 선택 날짜/시간이 예약과 겹침 여부
  const [overlapMsg, setOverlapMsg] = useState(''); // 사용자 안내 문구

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  useEffect(() => {
    if (locationKey === '') {
      setLocationKey(location.key);
    }

    if (locationKey !== '' && locationKey !== location.key) {
      window.location.reload();
    }
  }, [location.key]);

  useEffect(() => {
    axios
      .get(`https://ciuc.or.kr:8443/api/get/facility_code_list?page=${page}&pageSize=${pageSize}`)
      .then((res) => {
        const { totalItems, results } = res.data;
        const listTotal = Math.ceil(totalItems / pageSize);
        setTotalPages(listTotal);
        setTotalItems(totalItems);
        setFacility_code_list(results);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [currentPage, page]);

  // ✅ 선택 시설의 예약 리스트 불러오기
  useEffect(() => {
    if (!selectFacility?.facility_code) {
      setReservedList([]);
      return;
    }

    axios
      .get(`https://ciuc.or.kr:8443/api/get/facility_reserve_by_code?facility_code=${selectFacility.facility_code}`)
      .then((res) => {
        setReservedList(res.data.data || []);
      })
      .catch((err) => {
        console.error(err);
        setReservedList([]);
      });
  }, [selectFacility]);

  const toMin = (t) => {
    const [h, m] = t.split(':').map((v) => parseInt(v, 10));
    return h * 60 + (m || 0);
  };

  const isDateRangeOverlap = (aStart, aEnd, bStart, bEnd) => {
    const as = new Date(aStart);
    const ae = new Date(aEnd);
    const bs = new Date(bStart);
    const be = new Date(bEnd);
    return as <= be && ae >= bs;
  };

  const isTimeOverlap = (aStartTime, aEndTime, bStartTime, bEndTime) => {
    const aS = toMin(aStartTime);
    const aE = toMin(aEndTime);
    const bS = toMin(bStartTime);
    const bE = toMin(bEndTime);
    return aS < bE && aE > bS;
  };

  // ✅ 날짜+시간 겹침 체크(부모에서 최종)
  useEffect(() => {
    setIsOverlap(false);
    setOverlapMsg('');

    if (!selectFacility?.facility_code) return;
    if (!selectStart || !selectEnd) return;
    if (!startTime || !endTime) return;
    if (!reservedList?.length) return;

    const hit = reservedList.find((r) => {
      const dateHit = isDateRangeOverlap(selectStart, selectEnd, r.start_date, r.end_date);
      if (!dateHit) return false;
      return isTimeOverlap(startTime, endTime, r.start_time, r.end_time);
    });

    if (hit) {
      setIsOverlap(true);
      setOverlapMsg(`이미 예약된 시간입니다. (${hit.start_date} ~ ${hit.end_date}, ${hit.start_time}~${hit.end_time})`);
    }
  }, [selectFacility, selectStart, selectEnd, startTime, endTime, reservedList]);

  // start/end Date -> 문자열로 변환
  useEffect(() => {
    if (start !== null) {
      const year = start.getFullYear().toString();
      let month = (start.getMonth() + 1).toString();
      month = month.length === 1 ? '0' + month : month;
      let day = start.getDate().toString();
      day = day.length === 1 ? '0' + day : day;
      setSelectStart(`${year}-${month}-${day}`);
    }
    if (end !== null) {
      const year = end.getFullYear().toString();
      let month = (end.getMonth() + 1).toString();
      month = month.length === 1 ? '0' + month : month;
      let day = end.getDate().toString();
      day = day.length === 1 ? '0' + day : day;
      setSelectEnd(`${year}-${month}-${day}`);
    }
  }, [start, end]);

  useEffect(() => {
    if (selectStart !== '' && selectEnd !== '') {
      setIsDateCheck(true);
    }
  }, [selectStart, selectEnd]);

  const handleSubmit = async () => {
    if (selectFacility === null) return alert('시설을 선택해주세요.');
    if (selectStart === '') return alert('예약 시작일을 선택해주세요.');
    if (selectEnd === '') return alert('예약 종료일을 선택해주세요.');
    if (startTime === '') return alert('예약 시작시간을 선택해주세요.');
    if (endTime === '') return alert('예약 종료시간을 선택해주세요.');
    if (reservation_name === '') return alert('예약자를 입력해주세요.');
    if (phoneNumber === '') return alert('연락처를 입력해주세요.');

    const formData = new FormData();
    formData.append('facility_code', selectFacility.facility_code);
    formData.append('start_date', selectStart);
    formData.append('end_date', selectEnd);
    formData.append('start_time', startTime);
    formData.append('end_time', endTime);
    formData.append('reservation_name', reservation_name);
    formData.append('phoneNumber', phoneNumber);

    try {
      await axios.post('https://ciuc.or.kr:8443/api/post/facility_reserve_write', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      alert(`예약이 완료되었습니다.`);
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    const selectedIdx = parseInt(e.target.value, 10);
    const selectedFacility = facility_code_list.find((facility) => facility.idx === selectedIdx);
    setSelectFacility(selectedFacility);
    setStart(null);
    setEnd(null);
    setSelectStart('');
    setSelectEnd('');
    setStartTime('');
    setEndTime('');
    setIsDateCheck(false);
  };

  const handleStartTimeChange = (e) => setStartTime(e.target.value);
  const handleEndTimeChange = (e) => setEndTime(e.target.value);

  const startTimeOptions = timeData
    .filter((hour) => {
      if (endTime !== '') {
        const endHour = parseInt(endTime.split(':')[0], 10);
        return endHour > hour;
      }
      return true;
    })
    .map((hour) => (
      <option key={hour} value={`${hour}:00`}>
        {hour}:00
      </option>
    ));

  const endTimeOptions = timeData
    .filter((hour) => {
      if (startTime !== '') {
        const startHour = parseInt(startTime.split(':')[0], 10);
        return startHour < hour;
      }
      return true;
    })
    .map((hour) => (
      <option key={hour} value={`${hour}:00`}>
        {hour}:00
      </option>
    ));

  const handleMouseDown = (e) => {
    if (!isDateCheck) {
      e.preventDefault();
      alert('날짜를 선택해주세요.');
    }
  };

  const handleSelect = (num) => {
    if (select === num) setSelect(0);
    else setSelect(num);
  };

  const moveBoard = (cate) => {
    navigate(`/board/${cate}`, { state: { cate: cate } });
  };

  const movePage = (path) => navigate(path);
  const handlePage = (num) => setPage(num);

  const renderPagination = () => {
    const pages = [];

    pages.push(
      <div key="prev" className="arrow_btn">
        <button
          className="page_button prev"
          onClick={() => handlePage(page > 1 ? page - 1 : 1)}
          disabled={page === 1}
        ></button>
      </div>
    );

    const totalPagesToShow = totalPages === 0 ? 1 : totalPages;
    for (let i = 1; i <= totalPagesToShow; i++) {
      pages.push(
        <button key={i} className={`page_number ${page === i ? 'active' : ''}`} onClick={() => handlePage(i)}>
          {i}
        </button>
      );
    }

    pages.push(
      <div key="next" className="arrow_btn">
        <button
          className="page_button"
          onClick={() => handlePage(page < totalPages ? page + 1 : totalPages)}
          disabled={page === totalPages}
        ></button>
      </div>
    );

    return pages;
  };

  const scrollTop = (step) => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setStep(step);
  };

  return (
    <div className="sub_wrap">
      <div className="sub_back">
        {/* 상단 배너 */}
        <div className="sub_banner_area reservation">
          <div className="sub_banner_back">
            <div className="navi_text">
              <div className="home_icon_sub"></div>
              {'> '}보유시설{' > '}시설예약
            </div>
            <div className="banner_main_text">시설예약</div>
          </div>
        </div>

        {/* 상단 드롭다운 네비 */}
        <div className="navi_area">
          <div className="navi_back">
            <div className="navi_box" onClick={() => handleSelect(1)}>
              <div className="navi_main_text">보유시설</div>
              <div className="navi_arrow"></div>
              <div className={`navi_select_box ${select === 1 && 'active'}`}>
                <div className="select_row" onClick={() => moveBoard('notice')}>
                  알림 및 소식
                </div>
                <div className="select_row" onClick={() => movePage('/fusionSupportProgram')}>
                  사업분야
                </div>
                <div className="select_row" onClick={() => movePage('/inquiryinfo')}>
                  기업연구동
                </div>
                <div className="select_row" onClick={() => movePage('/meetingroom')}>
                  보유시설
                </div>
                <div className="select_row" onClick={() => movePage('/vision')}>
                  융합원소개
                </div>
              </div>
            </div>

            <div className="navi_box" onClick={() => handleSelect(2)}>
              <div className="navi_main_text">시설예약</div>
              <div className="navi_arrow"></div>
              <div className={`navi_select_box ${select === 2 && 'active'}`}>
                <div className="select_row" onClick={() => movePage('/meetingroom')}>
                  회의실
                </div>
                <div className="select_row" onClick={() => movePage('/computer')}>
                  전산실습실
                </div>
                <div className="select_row" onClick={() => movePage('/printing')}>
                  3D프린터
                </div>
                <div className="select_row" onClick={() => movePage('/arvr')}>
                  AR/VR
                </div>
                <div className="select_row" onClick={() => movePage('/cnc')}>
                  CNC/MCT
                </div>
                <div className="select_row" onClick={() => navigate('/board/facility/write', { state: { step: 1 } })}>
                  시설예약
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 메인 컨테이너 */}
        <div className="sub_main_area facility">
          <div className="facility_box">
            {/* 좌측 고정 사이드바 */}
            <aside className="intro_aside_fixed">
              <div className="intro_aside_header">
                <div className="center_box">
                  <span>보유시설</span>
                  <div className="center_line"></div>
                </div>
              </div>
              <ul className="intro_aside_list">
                <li className={`intro_aside_item ${isActive('/meetingroom')}`} onClick={() => movePage('/meetingroom')}>
                  <span>회의실</span>
                  <span className="aside_arrow">&gt;</span>
                </li>
                <li className={`intro_aside_item ${isActive('/computer')}`} onClick={() => movePage('/computer')}>
                  <span>전산실습실</span>
                  <span className="aside_arrow">&gt;</span>
                </li>
                <li className={`intro_aside_item ${isActive('/printing')}`} onClick={() => movePage('/printing')}>
                  <span>3D프린터</span>
                  <span className="aside_arrow">&gt;</span>
                </li>
                <li className={`intro_aside_item ${isActive('/arvr')}`} onClick={() => movePage('/arvr')}>
                  <span>AR / VR</span>
                  <span className="aside_arrow">&gt;</span>
                </li>
                <li className={`intro_aside_item ${isActive('/cnc')}`} onClick={() => movePage('/cnc')}>
                  <span>CNC / MCT</span>
                  <span className="aside_arrow">&gt;</span>
                </li>
                <li
                  className={`intro_aside_item ${isActive('/board/facility/write')}`}
                  onClick={() => movePage('/board/facility/write')}
                >
                  <span>시설예약</span>
                  <span className="aside_arrow">&gt;</span>
                </li>
              </ul>
            </aside>

            {/* STEP 1 : 시설 선택 */}
            {step === 1 && (
              <div className="facility_select_box">
                <div className="common_info">
                  <div className="info_title">이용안내</div>
                  <div className="info_content">
                    <ul>
                      <li className="content_title">강의장 및 대회의실 (VAT 별도)</li>
                      <li>1시간 미만은 1시간으로 계산</li>
                      <li>주말 및 공휴일 : 20% 가산</li>
                      <li>입주기업 : 50% 할인 / 가족기업 20% 할인</li>
                      <li>대학 및 융합원 주관 행사의 경우 무료로 사용</li>
                    </ul>
                    <ul>
                      <li className="content_title">부속설비</li>
                      <li>전자교탁, 빔 프로젝터 사용료 : 1시간 10,000원</li>
                      <li>
                        전산강의장 컴퓨터 사용료 : 1시간 20,000원
                        <p>※ 부속설비 사용 시 강의장 및 대회의실 사용 시간 전체에 대해 부과</p>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="facility_list_box">
                  {facility_code_list.length === 0 ? (
                    <div className="body_row">데이터가 존재하지 않습니다.</div>
                  ) : (
                    facility_code_list.map((facility) => (
                      <div
                        className={
                          'facility_item' + (selectFacility && selectFacility.idx === facility.idx ? ' active' : '')
                        }
                        key={facility.idx}
                      >
                        <div className="facility_item_image">
                          <img
                            className="facility_image"
                            src={`https://ciuc.or.kr:8443/${facility.file_url}`}
                            alt={facility.facility_name}
                          />
                        </div>

                        <div className="facility_item_info">
                          <div className="facility_item_header">
                            {/* ✅ 왼쪽: 배지(시설폼) + 호실(시설명) */}
                            <div className="facility_item_left">
                              <span className="facility_badge">{facility.facility_form}</span>
                              <span className="facility_room_no">{facility.facility_name}</span>
                            </div>

                            {/* ✅ 오른쪽: 신청 버튼만 */}
                            <div className="facility_item_header_right">
                              <button
                                type="button"
                                className="facility_apply_btn"
                                onClick={(e) => {
                                  e.stopPropagation(); // 혹시 상위 클릭 남아있을 때 방지
                                  setSelectFacility(facility);
                                  setStart(null);
                                  setEnd(null);
                                  setSelectStart('');
                                  setSelectEnd('');
                                  setStartTime('');
                                  setEndTime('');
                                  setIsDateCheck(false);

                                  scrollTop(2); // Step2로 이동
                                }}
                              >
                                신청하기
                              </button>
                            </div>
                          </div>

                          <div className="facility_item_detail">
                            <div className="facility_detail_grid">
                              <div className="detail_item">
                                <FacilityDetailIcon type="people" />
                                <span className="detail_text">최대 {facility.max_person}</span>
                              </div>
                              <div className="detail_item">
                                <FacilityDetailIcon type="fee" />
                                <span className="detail_text">{facility.facility_fee}</span>
                              </div>
                              <div className="detail_item">
                                <FacilityDetailIcon type="badge_red" />
                                <span className="detail_text">입주기업 50% 감면</span>
                              </div>
                              <div className="detail_item">
                                <FacilityDetailIcon type="badge_gray" />
                                <span className="detail_text">가족기업 20% 감면</span>
                              </div>

                              {/* ✅ 기존 JSX 유지: className은 facility_sub_row 그대로 */}
                              <div className="detail_item facility_sub_row">
                                <FacilityDetailIcon type="sub" />
                                <span className="detail_text">부속설비: {facility.sub_facility}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="pagination_box">{renderPagination()}</div>
              </div>
            )}

            {/* STEP 2 : 날짜/시간 선택 */}
            {step === 2 && (
              <div className="facility_first">
                <div className="facility_left">
                  <MyCalendar
                    start={start}
                    setStart={setStart}
                    end={end}
                    setEnd={setEnd}
                    selectFacility={selectFacility}
                    setSelectEnd={setSelectEnd}
                    reservedList={reservedList}
                  />
                </div>

                <div className="facility_right">
                  <div className="facility_inner_box">
                    <div className="facility_select_view">
                      <div className="facility_content">
                        <div className="facility_facility_title">시설 선택</div>
                        <div>
                          <select
                            className="facility_select"
                            onChange={handleChange}
                            value={selectFacility !== null ? selectFacility.idx : ''}
                          >
                            <option value="" disabled>
                              선택
                            </option>
                            {facility_code_list.map((facility) => (
                              <option key={facility.idx} value={facility.idx}>
                                {facility.facility_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="time_content">
                        <div className="facility_tile_title">시간 선택</div>
                        <div className="time_select_box">
                          <select
                            className="time_select"
                            value={startTime}
                            onChange={handleStartTimeChange}
                            onMouseDown={handleMouseDown}
                          >
                            <option value={''} disabled>
                              선택
                            </option>
                            {startTimeOptions}
                          </select>
                          <span>~</span>
                          <select
                            className="time_select"
                            value={endTime}
                            onChange={handleEndTimeChange}
                            onMouseDown={handleMouseDown}
                          >
                            <option value={''} disabled>
                              선택
                            </option>
                            {endTimeOptions}
                          </select>
                        </div>

                        {isOverlap && <div className="reserved_notice_error">{overlapMsg}</div>}
                      </div>
                    </div>

                    <div className="facility_view">
                      <div className="facility_view_content">
                        <div className="facility_title_view">선택한 시설</div>
                        <div className="facility_data">{selectFacility ? selectFacility.facility_name : ''}</div>
                      </div>

                      <div className="date_view_content">
                        <div className="date_title_view">선택한 날짜</div>
                        <div className="date_box">
                          <div className="date_start_data">{selectStart !== null ? selectStart : ''}</div>
                          <span>-</span>
                          <div className="date_end_data">{selectEnd !== null ? selectEnd : ''}</div>
                        </div>
                      </div>

                      <div className="row_btn_box">
                        <div className="back_btn" onClick={() => setStep(1)}>
                          돌아가기
                        </div>
                        <div
                          className="more_btn"
                          onClick={() => {
                            if (selectFacility === null) alert('시설을 선택해주세요.');
                            else if (!selectStart) alert('예약 시작일을 선택해주세요.');
                            else if (!selectEnd) alert('예약 종료일을 선택해주세요.');
                            else if (!startTime) alert('예약 시작시간을 선택해주세요.');
                            else if (!endTime) alert('예약 종료시간을 선택해주세요.');
                            else if (isOverlap) alert('이미 예약된 일정과 겹칩니다. 다른 날짜/시간을 선택해주세요.');
                            else scrollTop(3);
                          }}
                        >
                          다음단계
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 : 예약자 입력 */}
            {step === 3 && (
              <div className="facility_second">
                <div className="facility_left">
                  <div className="facility_title">{selectFacility.facility_name}</div>
                  <div className="facility_image_box">
                    {selectFacility.file_url ? (
                      <img
                        className="facility_image"
                        src={`https://ciuc.or.kr:8443/${selectFacility.file_url}`}
                        alt="시설 이미지"
                      />
                    ) : (
                      <div className="facility_image_none"></div>
                    )}
                  </div>
                  <div className="facility_detail">
                    <ul>
                      <li>
                        <span className="label">인원</span>
                        <span className="value">{selectFacility.facility_form}</span>
                      </li>
                      <li>
                        <span className="label">용도</span>
                        <span className="value">{selectFacility.max_person}</span>
                      </li>
                      <li>
                        <span className="label">시설</span>
                        <span className="value">{selectFacility.facility_fee}</span>
                      </li>
                      <li>
                        <span className="label">위치</span>
                        <span className="value">{selectFacility.sub_facility}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="facility_right">
                  <div className="facility_input">
                    <div className="input_content">
                      <div className="input_label">예약자</div>
                      <input
                        className="input_text"
                        placeholder="예약자 대표 성함을 입력해주세요."
                        value={reservation_name}
                        type="text"
                        onChange={(e) => setReservation_name(e.target.value)}
                      />
                    </div>

                    <div className="input_content">
                      <div className="input_label">연락처</div>
                      <input
                        placeholder="연락처를 입력해주세요."
                        className="input_text"
                        type="text"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                        maxLength={13}
                      />
                    </div>

                    <div className="facility_row_btn_box">
                      <div className="back_btn" onClick={() => setStep(2)}>
                        돌아가기
                      </div>
                      <div className="more_btn" onClick={handleSubmit}>
                        예약 신청하기
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={topRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityWrite;
